import { toast } from 'react-toastify';
import api from './axiosConfig';

/**
 * Check for student scheduling conflicts
 * @param {number} studentId - Student ID
 * @param {string} startTime - Start time in format "HH:MM"
 * @param {string} endTime - End time in format "HH:MM"
 * @param {number|null} excludeInterviewId - Interview ID to exclude from conflict check
 * @param {string} date - Date in format "YYYY-MM-DD"
 * @returns {Promise<boolean|Array>} - False if no conflicts, otherwise conflicts array
 */
export const checkStudentTimeConflict = async (studentId, startTime, endTime, excludeInterviewId = null, date) => {
  try {
    // Call API to check for conflicts
    const response = await api.get(`/scheduling/check-student-conflicts/`, {
      params: {
        student_id: studentId,
        start_time: startTime,
        end_time: endTime,
        exclude_interview_id: excludeInterviewId,
        date: date
      }
    });
    
    return response.data.conflicts.length > 0 ? response.data.conflicts : false;
  } catch (error) {
    console.error('Error checking for conflicts:', error);
    toast.error('Failed to check for scheduling conflicts');
    return false;
  }
};

/**
 * Find an interview in a specific cell
 * @param {string} droppableId - Cell ID in format "cell-{timeSlotId}-{panelNumber}"
 * @param {Function} getSelectedCompanyDetails - Function to get company details
 * @param {Array} scheduledInterviews - List of scheduled interviews
 * @returns {Object|null} - Interview object or null if not found
 */
export const findInterviewInCell = (droppableId, getSelectedCompanyDetails, scheduledInterviews) => {
  if (!droppableId.startsWith('cell-')) return null;
  
  const [, timeSlotId, panelNumber] = droppableId.split('-');
  const company = getSelectedCompanyDetails();
  const round = company?.rounds?.find((r) =>
    r.panels.some((p) => p.panel_number === parseInt(panelNumber))
  );
  
  if (!round) return null;
  
  return scheduledInterviews.find(
    (i) =>
      i.round.number === round.round_number &&
      i.panel.number === parseInt(panelNumber) &&
      i.time_slot.id === parseInt(timeSlotId)
  );
};

/**
 * Handle dragging from unscheduled list to a schedule cell
 */
export const handleUnscheduledToCell = async (studentId, destinationCellId, data, helpers) => {
  console.log('handleUnscheduledToCell called with:', {
    studentId,
    destinationCellId,
    unscheduledStudentsCount: data.unscheduledStudents?.length
  });

  const { 
    unscheduledStudents, 
    selectedCompany, 
    selectedDate 
  } = data;
  
  const { 
    getSelectedCompanyDetails,
    generateTimeSlots, 
    setConflictInfo, 
    setShowConflictModal,
    findInterviewInCell: findInterview,
    updateInterviewStatus,
    assignInterview,
    fetchScheduleData,
    formatTime
  } = helpers;

  // Find the unscheduled student
  const unscheduledStudent = unscheduledStudents.find(
    (s) => s.student.id === studentId
  );
  
  console.log('Looking for student with ID:', studentId);
  console.log('Available unscheduled students:', unscheduledStudents.map(s => ({
    id: s.student.id,
    name: s.student.name
  })));
  
  if (!unscheduledStudent) {
    console.error('Student not found in unscheduled list. Available IDs:', 
      unscheduledStudents.map(s => s.student.id));
    throw new Error(`Student with ID ${studentId} not found in unscheduled list`);
  }
  
  console.log('Found unscheduled student:', unscheduledStudent.student.name);
  
  // Parse destination cell details
  const [, timeSlotId, panelNumber] = destinationCellId.split('-');
  
  // Find the round, time slot, and panel
  const company = getSelectedCompanyDetails();
  const round = company?.rounds?.find((r) =>
    r.panels.some((p) => p.panel_number === parseInt(panelNumber))
  );
  const timeSlot = round
    ? generateTimeSlots(round).find((ts) => ts.id === parseInt(timeSlotId))
    : null;
  const panel = round?.panels?.find(
    (p) => p.panel_number === parseInt(panelNumber)
  );
  
  if (!round || !timeSlot || !panel) {
    throw new Error('Invalid round, time slot, or panel');
  }
  
  // Check if the destination cell is occupied
  const existingInterview = findInterview(
    destinationCellId,
    getSelectedCompanyDetails,
    data.scheduledInterviews
  );
  
  // Check for time conflicts for this student
  const hasTimeConflict = await checkStudentTimeConflict(
    unscheduledStudent.student.id, 
    timeSlot.startTime,
    timeSlot.endTime,
    null,
    selectedDate.toISOString().split('T')[0]
  );
  
  if (hasTimeConflict && !existingInterview) {
    // Show conflict modal
    setConflictInfo({
      type: 'TIME_CONFLICT',
      student: unscheduledStudent.student,
      timeSlot: timeSlot,
      conflictDetails: hasTimeConflict,
      destinationCellId: destinationCellId,
      panelNumber: panel.panel_number
    });
    setShowConflictModal(true);
    return;
  }
  
  // Prepare assignment data
  const assignmentData = {
    student_id: unscheduledStudent.student.id,
    company_id: parseInt(selectedCompany),
    round_id: round.id,
    panel_id: panel.id,
    time_slot_id: timeSlot.id
  };
  
  // If there's an existing interview, we need to handle the swap
  if (existingInterview) {
    // Check for time conflicts for the student being swapped out
    const swapConflict = await checkStudentTimeConflict(
      existingInterview.student.id,
      timeSlot.startTime,
      timeSlot.endTime,
      existingInterview.id,
      selectedDate.toISOString().split('T')[0]
    );
    
    if (swapConflict) {
      // Show swap conflict modal
      setConflictInfo({
        type: 'SWAP_CONFLICT',
        student: unscheduledStudent.student,
        existingStudent: existingInterview.student,
        timeSlot: timeSlot,
        conflictDetails: swapConflict
      });
      setShowConflictModal(true);
      return;
    }
    
    // Move the existing interview to unscheduled
    await updateInterviewStatus(existingInterview.id, 'UNSCHEDULED');
  }
  
  try {
    // The backend will automatically handle updating existing UNSCHEDULED interviews
    await assignInterview(assignmentData);
    
    // Update the local state
    await fetchScheduleData(); // Refresh data from server
    
    toast.success(
      `Scheduled ${unscheduledStudent.student.name} for ${formatTime(timeSlot.startTime)}${
        existingInterview ? `, unscheduled ${existingInterview.student.name}` : ''
      }`
    );
  } catch (error) {
    // Check if this is a 409 conflict error
    if (error.status === 409 || error.response?.status === 409) {
      const errorData = error.response?.data || error;
      
      // Show conflict modal for any backend conflicts
      if (errorData.conflicts || errorData.conflict) {
        setConflictInfo({
          type: 'TIME_CONFLICT',
          student: unscheduledStudent.student,
          timeSlot: timeSlot,
          conflictDetails: errorData.conflicts || [errorData.conflict],
          destinationCellId: destinationCellId,
          panelNumber: panel.panel_number,
          errorMessage: errorData.message
        });
        setShowConflictModal(true);
        return;
      }
    }
    
    // Check for any error that mentions panel or conflict and convert to modal
    const errorMessage = error.message || error.response?.data?.message || '';
    if (errorMessage.toLowerCase().includes('panel already has') ||
        errorMessage.toLowerCase().includes('scheduled for this time') ||
        errorMessage.toLowerCase().includes('conflict')) {
      
      setConflictInfo({
        type: 'TIME_CONFLICT',
        student: unscheduledStudent.student,
        timeSlot: timeSlot,
        conflictDetails: [{ 
          company_name: company.name || 'Unknown Company',
          start_time: timeSlot.startTime,
          end_time: timeSlot.endTime,
          message: errorMessage
        }],
        destinationCellId: destinationCellId,
        panelNumber: panel.panel_number,
        errorMessage: errorMessage
      });
      setShowConflictModal(true);
      return;
    }
    
    // For non-conflict errors, still throw
    throw error;
  }
};

/**
 * Handle dragging from one cell to another
 */
export const handleCellToCell = async (interviewId, sourceCellId, destinationCellId, data, helpers) => {
    console.log('handleCellToCell called with:', {
      interviewId,
      sourceCellId,
      destinationCellId,
      scheduledInterviewsCount: data.scheduledInterviews?.length
    });

    const { scheduledInterviews, selectedCompany, selectedDate } = data;
    
    const { 
      getSelectedCompanyDetails,
      generateTimeSlots, 
      setConflictInfo,
      setShowConflictModal,
      checkStudentTimeConflict,
      findInterviewInCell: findInterview,
      moveInterview,
      fetchScheduleData,
      formatTime
    } = helpers;
  
    const draggedInterview = scheduledInterviews.find(
      (i) => i.id === interviewId
    );
    
    console.log('Looking for interview with ID:', interviewId);
    console.log('Available interviews:', scheduledInterviews.map(i => ({
      id: i.id,
      student: i.student.name
    })));
    
    if (!draggedInterview) {
      console.error('Interview not found:', interviewId, 'Available IDs:', 
        scheduledInterviews.map(i => i.id));
      toast.error(`Interview with ID ${interviewId} not found`);
      return;
    }
    
    console.log('Found interview:', draggedInterview.student.name);
    
    // Parse destination cell details
    const destParts = destinationCellId.split('-');
    if (destParts.length !== 3 || destParts[0] !== 'cell') {
      console.error('Invalid destinationCellId format:', destinationCellId);
      toast.error('Invalid destination cell');
      return;
    }
    const [, destTimeSlotId, destPanelNumber] = destParts;
    const panelNumber = parseInt(destPanelNumber);
    
    // Find the round, time slot, and panel
    const company = getSelectedCompanyDetails();
    if (!company) {
      console.error('No company selected');
      toast.error('No company selected');
      return;
    }
    
    const destRound = company.rounds?.find((r) =>
      r.panels.some((p) => p.panel_number === panelNumber)
    );
    if (!destRound) {
      console.error('Round not found for panel:', panelNumber);
      toast.error('Invalid round');
      return;
    }
    
    const destTimeSlot = generateTimeSlots(destRound).find(
      (ts) => ts.id === parseInt(destTimeSlotId)
    );
    if (!destTimeSlot) {
      console.error('Time slot not found:', destTimeSlotId);
      toast.error('Invalid time slot');
      return;
    }
    
    // Try to find panel from company data
    let destPanel = destRound.panels.find((p) => p.panel_number === panelNumber);
    
    // Fallback: Use scheduledInterviews to find panel_id
    if (!destPanel) {
      const interviewWithPanel = scheduledInterviews.find(
        (i) => i.panel.number === panelNumber && i.round.id === destRound.id
      );
      if (interviewWithPanel) {
        destPanel = {
          id: interviewWithPanel.panel.id,
          panel_number: interviewWithPanel.panel.number
        };
        console.warn('Used scheduledInterviews for panel:', destPanel);
      } else {
        console.error('Panel not found for number:', panelNumber, 'in round:', destRound.id);
        toast.error('Invalid panel');
        return;
      }
    }
    
    // Check for time conflicts
    const hasTimeConflict = await checkStudentTimeConflict(
      draggedInterview.student.id, 
      destTimeSlot.startTime,
      destTimeSlot.endTime,
      draggedInterview.id,
      selectedDate.toISOString().split('T')[0]
    );
    
    if (hasTimeConflict && !findInterview(destinationCellId, getSelectedCompanyDetails, scheduledInterviews)) {
      setConflictInfo({
        type: 'TIME_CONFLICT',
        student: draggedInterview.student,
        timeSlot: destTimeSlot,
        conflictDetails: hasTimeConflict,
        destinationCellId: destinationCellId,
        panelNumber: panelNumber
      });
      setShowConflictModal(true);
      return;
    }
    
    // Prepare move data
    const moveData = {
      round_id: destRound.id,
      panel_id: destPanel.id,
      time_slot_id: destTimeSlot.id
    };
    
    console.log('moveInterview payload:', {
      interviewId,
      moveData,
      destinationCellId
    });
    
    // Handle swap or single move
    const existingInterview = findInterview(destinationCellId, getSelectedCompanyDetails, scheduledInterviews);
    
    if (existingInterview) {
      const sourceParts = sourceCellId.split('-');
      if (sourceParts.length !== 3 || sourceParts[0] !== 'cell') {
        console.error('Invalid sourceCellId format:', sourceCellId);
        toast.error('Invalid source cell');
        return;
      }
      const [, sourceTimeSlotId, sourcePanelNumber] = sourceParts;
      const sourcePanelNum = parseInt(sourcePanelNumber);
      
      const sourceRound = company.rounds?.find((r) =>
        r.panels.some((p) => p.panel_number === sourcePanelNum)
      );
      if (!sourceRound) {
        console.error('Source round not found for panel:', sourcePanelNum);
        toast.error('Invalid source round');
        return;
      }
      
      const sourceTimeSlot = generateTimeSlots(sourceRound).find(
        (ts) => ts.id === parseInt(sourceTimeSlotId)
      );
      if (!sourceTimeSlot) {
        console.error('Source time slot not found:', sourceTimeSlotId);
        toast.error('Invalid source time slot');
        return;
      }
      
      let sourcePanel = sourceRound.panels.find((p) => p.panel_number === sourcePanelNum);
      if (!sourcePanel) {
        const sourceInterview = scheduledInterviews.find(
          (i) => i.panel.number === sourcePanelNum && i.round.id === sourceRound.id
        );
        if (sourceInterview) {
          sourcePanel = {
            id: sourceInterview.panel.id,
            panel_number: sourceInterview.panel.number
          };
          console.warn('Used scheduledInterviews for source panel:', sourcePanel);
        } else {
          console.error('Source panel not found:', sourcePanelNum);
          toast.error('Invalid source panel');
          return;
        }
      }
      
      const swapConflict = await checkStudentTimeConflict(
        existingInterview.student.id,
        sourceTimeSlot.startTime,
        sourceTimeSlot.endTime,
        existingInterview.id,
        selectedDate.toISOString().split('T')[0]
      );
      
      if (swapConflict) {
        setConflictInfo({
          type: 'SWAP_CONFLICT',
          student: draggedInterview.student,
          existingStudent: existingInterview.student,
          timeSlot: destTimeSlot,
          sourceTimeSlot: sourceTimeSlot,
          conflictDetails: swapConflict,
          destinationCellId: destinationCellId,
          sourceCellId: sourceCellId
        });
        setShowConflictModal(true);
        return;
      }
      
      try {
        // Perform the swap
        await moveInterview(draggedInterview.id, moveData);
        
        const sourceMoveData = {
          round_id: sourceRound.id,
          panel_id: sourcePanel.id,
          time_slot_id: sourceTimeSlot.id
        };
        
        console.log('swap moveInterview payload:', {
          interviewId: existingInterview.id,
          sourceMoveData,
          sourceCellId
        });
        
        await moveInterview(existingInterview.id, sourceMoveData);
        
        toast.success(
          `Swapped ${draggedInterview.student.name} with ${existingInterview.student.name}`
        );
      } catch (error) {
        // Check if this is a 409 conflict error for swap
        if (error.status === 409 || error.response?.status === 409) {
          const errorData = error.response?.data || error;
          
          if (errorData.conflicts || errorData.conflict) {
            setConflictInfo({
              type: 'SWAP_CONFLICT',
              student: draggedInterview.student,
              existingStudent: existingInterview.student,
              timeSlot: destTimeSlot,
              sourceTimeSlot: sourceTimeSlot,
              conflictDetails: errorData.conflicts || [errorData.conflict],
              destinationCellId: destinationCellId,
              sourceCellId: sourceCellId,
              panelNumber: panelNumber,
              errorMessage: errorData.message
            });
            setShowConflictModal(true);
            return;
          }
        }
        
        // Check for any error that mentions panel or conflict and convert to modal
        const errorMessage = error.message || error.response?.data?.message || '';
        if (errorMessage.toLowerCase().includes('panel already has') ||
            errorMessage.toLowerCase().includes('scheduled for this time') ||
            errorMessage.toLowerCase().includes('conflict')) {
          
          setConflictInfo({
            type: 'SWAP_CONFLICT',
            student: draggedInterview.student,
            existingStudent: existingInterview.student,
            timeSlot: destTimeSlot,
            sourceTimeSlot: sourceTimeSlot,
            conflictDetails: [{ 
              company_name: company.name || 'Unknown Company',
              start_time: destTimeSlot.startTime,
              end_time: destTimeSlot.endTime,
              message: errorMessage
            }],
            destinationCellId: destinationCellId,
            sourceCellId: sourceCellId,
            panelNumber: panelNumber,
            errorMessage: errorMessage
          });
          setShowConflictModal(true);
          return;
        }
        
        // For non-conflict errors, still throw
        throw error;
      }
    } else {
      try {
        await moveInterview(draggedInterview.id, moveData);
        toast.success(
          `Rescheduled ${draggedInterview.student.name} to ${formatTime(destTimeSlot.startTime)}`
        );
      } catch (error) {
        // Check if this is a 409 conflict error for simple move
        if (error.status === 409 || error.response?.status === 409) {
          const errorData = error.response?.data || error;
          
          if (errorData.conflicts || errorData.conflict) {
            setConflictInfo({
              type: 'TIME_CONFLICT',
              student: draggedInterview.student,
              timeSlot: destTimeSlot,
              conflictDetails: errorData.conflicts || [errorData.conflict],
              destinationCellId: destinationCellId,
              panelNumber: panelNumber,
              errorMessage: errorData.message
            });
            setShowConflictModal(true);
            return;
          }
        }
        
        // Check for any error that mentions panel or conflict and convert to modal
        const errorMessage = error.message || error.response?.data?.message || '';
        if (errorMessage.toLowerCase().includes('panel already has') ||
            errorMessage.toLowerCase().includes('scheduled for this time') ||
            errorMessage.toLowerCase().includes('conflict')) {
          
          setConflictInfo({
            type: 'TIME_CONFLICT',
            student: draggedInterview.student,
            timeSlot: destTimeSlot,
            conflictDetails: [{ 
              company_name: company.name || 'Unknown Company',
              start_time: destTimeSlot.startTime,
              end_time: destTimeSlot.endTime,
              message: errorMessage
            }],
            destinationCellId: destinationCellId,
            panelNumber: panelNumber,
            errorMessage: errorMessage
          });
          setShowConflictModal(true);
          return;
        }
        
        // For non-conflict errors, still throw
        throw error;
      }
    }
    
    await fetchScheduleData();
  };
/**
 * Handle dragging from a cell to the unscheduled list
 */
export const handleCellToUnscheduled = async (interviewId, data, helpers) => {
  console.log('handleCellToUnscheduled called with:', {
    interviewId,
    scheduledInterviewsCount: data.scheduledInterviews?.length
  });

  const { scheduledInterviews } = data;
  const { updateInterviewStatus, fetchScheduleData } = helpers;

  const interview = scheduledInterviews.find((i) => i.id === interviewId);
  
  console.log('Looking for interview to unschedule with ID:', interviewId);
  console.log('Available interviews:', scheduledInterviews.map(i => ({
    id: i.id,
    student: i.student.name
  })));
  
  if (!interview) {
    console.error('Interview not found for unscheduling:', interviewId);
    throw new Error(`Interview with ID ${interviewId} not found`);
  }
  
  console.log('Found interview to unschedule:', interview.student.name);
  
  // Mark as unscheduled instead of cancelled
  await updateInterviewStatus(interview.id, 'UNSCHEDULED');
  
  // Update the local state
  await fetchScheduleData(); // Refresh data from server
  
  toast.success(`Moved ${interview.student.name} to unscheduled`);
};